import { getElectronAPI } from "@/plugins/desktop";

const DEFAULT_CONFIG = Object.freeze({
  habilitada: false,
  modelo: "toledo_prix_3_fit",
  portaSerial: "",
  adaptadorId: "",
  baudRate: 9600,
  casasDecimais: 3,
  toleranciaG: 5,
  amostrasJanela: 8,
  pesoMinimoG: 5,
  intervaloMs: 200,
});

const KEYS = Object.freeze({
  habilitada: "balanca_checkout_habilitada",
  modelo: "balanca_checkout_modelo",
  portaSerial: "balanca_checkout_porta_serial",
  adaptadorId: "balanca_checkout_adaptador_id",
  baudRate: "balanca_checkout_baud_rate",
  casasDecimais: "balanca_checkout_casas_decimais",
  toleranciaG: "balanca_checkout_tolerancia_g",
  amostrasJanela: "balanca_checkout_amostras_janela",
  pesoMinimoG: "balanca_checkout_peso_minimo_g",
  intervaloMs: "balanca_checkout_intervalo_ms",
});

const NUMERIC_KEYS = [
  "baudRate",
  "casasDecimais",
  "toleranciaG",
  "amostrasJanela",
  "pesoMinimoG",
  "intervaloMs",
];

function isTrue(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function normalizar(config = {}) {
  const value = { ...DEFAULT_CONFIG, ...config };
  value.habilitada = isTrue(value.habilitada);
  value.portaSerial = String(value.portaSerial || "").trim();
  value.adaptadorId = String(value.adaptadorId || "").trim();
  NUMERIC_KEYS.forEach((key) => {
    const parsed = Number(value[key]);
    value[key] = Number.isFinite(parsed) ? parsed : DEFAULT_CONFIG[key];
  });
  return value;
}

function normalizarErroElectron(error, fallback) {
  const mensagem = String(error?.message || error || fallback || "");
  const limpa = mensagem
    .replace(/^Error invoking remote method '[^']+':\s*/i, "")
    .replace(/^Error:\s*/i, "")
    .trim();
  return new Error(limpa || fallback || "Falha na comunicação com a balança.");
}

const BalancaCheckoutService = {
  _api() {
    const api = getElectronAPI();
    if (!api?.balanca) {
      throw new Error("Integração com balança indisponível neste aplicativo.");
    }
    return api;
  },

  async carregarConfiguracao() {
    const api = this._api();
    const entries = await Promise.all(
      Object.entries(KEYS).map(async ([campo, key]) => [
        campo,
        await api.config.get(key),
      ])
    );
    const stored = Object.fromEntries(
      entries.filter(([, value]) => value !== null && value !== undefined)
    );
    return normalizar(stored);
  },

  async salvarConfiguracao(config = {}) {
    const api = this._api();
    const value = normalizar(config);
    await Promise.all(
      Object.entries(KEYS).map(([campo, key]) =>
        api.config.set(
          key,
          campo === "habilitada" ? (value[campo] ? "1" : "0") : value[campo]
        )
      )
    );
    return value;
  },

  async listarPortas() {
    try {
      return await this._api().balanca.listarPortas();
    } catch (error) {
      throw normalizarErroElectron(error, "Não foi possível listar as portas.");
    }
  },

  async listarModelos() {
    try {
      return await this._api().balanca.listarModelos();
    } catch (error) {
      throw normalizarErroElectron(
        error,
        "Não foi possível listar os modelos."
      );
    }
  },

  async conectar(config) {
    try {
      return await this._api().balanca.conectar(normalizar(config));
    } catch (error) {
      throw normalizarErroElectron(
        error,
        "Não foi possível conectar à balança."
      );
    }
  },

  desconectar() {
    return this._api().balanca.desconectar();
  },

  async diagnostico(config) {
    try {
      return await this._api().balanca.diagnostico(
        config ? normalizar(config) : null
      );
    } catch (error) {
      throw normalizarErroElectron(
        error,
        "Não foi possível consultar a balança."
      );
    }
  },

  async lerPeso(config) {
    try {
      return await this._api().balanca.lerPeso(
        config ? normalizar(config) : null
      );
    } catch (error) {
      throw normalizarErroElectron(error, "Não foi possível ler o peso.");
    }
  },

  async detectar(config) {
    try {
      return await this._api().balanca.detectar(normalizar(config));
    } catch (error) {
      throw normalizarErroElectron(error, "A balança não foi detectada.");
    }
  },

  onEvento(callback) {
    return this._api().balanca.onEvento(callback);
  },
};

export { DEFAULT_CONFIG };
export default BalancaCheckoutService;
